import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StarIcon, ArrowRightIcon, CheckCircleIcon, PlayIcon, LayersIcon, ZapIcon, UsersIcon } from 'lucide-react';
import { CompanyLogos } from './CompanyLogos';
interface HeroSectionProps {
  onOpenOnboarding?: () => void;
}
const lessons = [{
  title: 'Design Thinking Fundamentals',
  duration: '45 min',
  done: true
}, {
  title: 'Figma Auto Layout Mastery',
  duration: '1h 20 min',
  done: true
}, {
  title: 'Component Variants Deep Dive',
  duration: '55 min',
  done: false,
  active: true
}, {
  title: 'Building a Design System',
  duration: '2h 10 min',
  done: false
}, {
  title: 'Prototyping & Interactions',
  duration: '1h 05 min',
  done: false
}];
const tabs = ['Course', 'Projects', 'Community'];
function HeroDashboard() {
  const [activeTab, setActiveTab] = useState(0);
  const [hoveredLesson, setHoveredLesson] = useState<number | null>(null);
  return <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0d0d0d] shadow-2xl shadow-black/60" data-id="element-158">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-3 bg-[#161616] border-b border-white/5" data-id="element-159">
        <span className="w-3 h-3 rounded-full bg-red-500/70" data-id="element-160" />
        <span className="w-3 h-3 rounded-full bg-yellow-500/70" data-id="element-161" />
        <span className="w-3 h-3 rounded-full bg-green-500/70" data-id="element-162" />
        <div className="ml-3 flex-1 max-w-xs mx-auto bg-[#0d0d0d] rounded-md px-3 py-1 text-xs text-gray-500 text-center" data-id="element-163">
          learndesign.app
        </div>
      </div>

      <div className="p-5" data-id="element-164">
        {/* Header row */}
        <div className="flex items-center justify-between mb-5" data-id="element-165">
          <div data-id="element-166">
            <p className="text-xs text-gray-500 mb-0.5" data-id="element-167">Week 3 of 12</p>
            <h3 className="text-base font-semibold text-white" data-id="element-168">
              Figma Mastery
            </h3>
          </div>
          {/* Progress ring */}
          <div className="relative w-14 h-14" data-id="element-169">
            <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56" data-id="element-170">
              <circle cx="28" cy="28" r="22" fill="none" stroke="#1a1a1a" strokeWidth="4" data-id="element-171" />
              <motion.circle cx="28" cy="28" r="22" fill="none" stroke="#fff200" strokeWidth="4" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 22}`} initial={{
              strokeDashoffset: 2 * Math.PI * 22
            }} animate={{
              strokeDashoffset: 2 * Math.PI * 22 * (1 - 0.38)
            }} transition={{
              duration: 1.5,
              delay: 0.8,
              ease: 'easeOut'
            }} data-id="element-172" />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-brand-yellow" data-id="element-173">
              38%
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-[#161616] rounded-lg p-1" data-id="element-174">
          {tabs.map((tab, i) => <button key={tab} onClick={() => setActiveTab(i)} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${activeTab === i ? 'bg-brand-yellow text-dark-900' : 'text-gray-500 hover:text-gray-300'}`} data-id="element-175">
              {tab}
            </button>)}
        </div>

        {/* Tab content */}
        <AnimatePresence mode="wait" data-id="element-176">
          {activeTab === 0 && <motion.div key="course" initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.2
        }} className="space-y-2" data-id="element-177">
              {lessons.map((lesson, i) => <motion.div key={lesson.title} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.07 + 0.1
          }} onMouseEnter={() => setHoveredLesson(i)} onMouseLeave={() => setHoveredLesson(null)} className={`flex items-center gap-3 p-2.5 rounded-lg transition-all duration-200 cursor-pointer ${lesson.active ? 'bg-brand-yellow/10 border border-brand-yellow/20' : hoveredLesson === i ? 'bg-white/5' : 'bg-transparent'}`} data-id="element-178">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${lesson.done ? 'bg-green-500/20' : lesson.active ? 'bg-brand-yellow/20' : 'bg-white/5'}`} data-id="element-179">
                    {lesson.done ? <CheckCircleIcon className="w-4 h-4 text-green-400" data-id="element-180" /> : lesson.active ? <PlayIcon className="w-3 h-3 text-brand-yellow ml-0.5" data-id="element-181" /> : <span className="w-2 h-2 rounded-full bg-gray-600" data-id="element-182" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-183">
                    <p className={`text-xs font-medium truncate ${lesson.done ? 'text-gray-500 line-through' : lesson.active ? 'text-white' : 'text-gray-400'}`} data-id="element-184">
                      {lesson.title}
                    </p>
                  </div>
                  <span className="text-xs text-gray-600 flex-shrink-0" data-id="element-185">
                    {lesson.duration}
                  </span>
                </motion.div>)}
            </motion.div>}

          {activeTab === 1 && <motion.div key="projects" initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.2
        }} className="space-y-2" data-id="element-186">
              {[{
            name: 'Mobile Banking App',
            status: 'In Progress',
            pct: 65,
            color: 'bg-blue-400'
          }, {
            name: 'SaaS Dashboard',
            status: 'Submitted',
            pct: 100,
            color: 'bg-green-400'
          }, {
            name: 'E-commerce Redesign',
            status: 'Not Started',
            pct: 0,
            color: 'bg-gray-600'
          }].map((p, i) => <motion.div key={p.name} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.1
          }} className="p-3 rounded-lg bg-white/5 border border-white/5" data-id="element-187">
                  <div className="flex items-center justify-between mb-2" data-id="element-188">
                    <span className="text-xs font-medium text-white" data-id="element-189">
                      {p.name}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.pct === 100 ? 'bg-green-500/20 text-green-400' : p.pct > 0 ? 'bg-brand-yellow/20 text-brand-yellow' : 'bg-white/10 text-gray-500'}`} data-id="element-190">
                      {p.status}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden" data-id="element-191">
                    <motion.div className={`h-full rounded-full ${p.color}`} initial={{
                width: 0
              }} animate={{
                width: `${p.pct}%`
              }} transition={{
                duration: 0.8,
                delay: i * 0.15 + 0.3
              }} data-id="element-192" />
                  </div>
                </motion.div>)}
            </motion.div>}

          {activeTab === 2 && <motion.div key="community" initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.2
        }} className="space-y-2" data-id="element-193">
              {[{
            src: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=100&h=100&fit=crop&crop=face',
            name: 'Amara K.',
            msg: 'Just finished the Figma components module 🔥',
            time: '2m ago'
          }, {
            src: 'https://images.unsplash.com/photo-1506277886164-e25aa3f4ef7f?w=100&h=100&fit=crop&crop=face',
            name: 'David J.',
            msg: 'Anyone want feedback on my portfolio?',
            time: '8m ago'
          }, {
            src: 'https://images.unsplash.com/photo-1589156280159-27698a70f29e?w=100&h=100&fit=crop&crop=face',
            name: 'Priya S.',
            msg: 'Mentor session with @Sarah was incredible!',
            time: '15m ago'
          }].map((m, i) => <motion.div key={m.name} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.1
          }} className="flex gap-3 p-2.5 rounded-lg bg-white/5" data-id="element-194">
                  <img src={m.src} alt={m.name} className="w-7 h-7 rounded-full flex-shrink-0 object-cover" data-id="element-195" />
                  <div className="flex-1 min-w-0" data-id="element-196">
                    <div className="flex items-center gap-2 mb-0.5" data-id="element-197">
                      <span className="text-xs font-semibold text-white" data-id="element-198">
                        {m.name}
                      </span>
                      <span className="text-xs text-gray-600" data-id="element-199">{m.time}</span>
                    </div>
                    <p className="text-xs text-gray-400 truncate" data-id="element-200">{m.msg}</p>
                  </div>
                </motion.div>)}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}
export function HeroSection({
  onOpenOnboarding
}: HeroSectionProps) {
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-dark-900 pt-20" data-id="element-201">
      {/* Background Elements */}
      <div className="absolute inset-0" data-id="element-202">
        <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-brand-yellow/10 rounded-full blur-3xl" data-id="element-203" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-brand-yellow/5 rounded-full blur-3xl" data-id="element-204" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} data-id="element-205" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-206">
        <div className="text-center" data-id="element-207">
          {/* Badge */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-yellow/10 border border-brand-yellow/20 mb-8" data-id="element-208">
            <span className="w-2 h-2 rounded-full bg-brand-yellow animate-pulse" data-id="element-209" />
            <span className="text-sm font-medium text-brand-yellow" data-id="element-210">
              The Best Way to Learn and Grow
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6" data-id="element-211">
            <span className="text-white" data-id="element-212">Become a </span>
            <span className="text-gradient-yellow" data-id="element-213">World-Class</span>
            <br data-id="element-214" />
            <span className="text-white" data-id="element-215">Product Designer</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-216">
            Master Figma, Framer, and AI-powered design. Join 100+ ambitious
            professionals who're staying ahead in their careers with our
            personalized, project-based curriculum.
          </motion.p>

          {/* Social Proof */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="flex flex-wrap items-center justify-center gap-6 mb-10" data-id="element-217">
            <div className="flex items-center" data-id="element-218">
              <div className="ml-4 text-left" data-id="element-219">
                <div className="flex items-center gap-1" data-id="element-220">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-brand-yellow text-brand-yellow" data-id="element-221" />)}
                  <span className="ml-1 text-sm font-semibold text-white" data-id="element-222">
                    4.9
                  </span>
                </div>
                <p className="text-sm text-gray-500" data-id="element-223">
                  Trusted by 100+ designers
                </p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-10 bg-white/10" data-id="element-224" />
            <div className="flex items-center gap-2 text-sm text-gray-400" data-id="element-225">
              <svg className="w-5 h-5 text-brand-yellow" fill="currentColor" viewBox="0 0 20 20" data-id="element-226">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-227" />
              </svg>
              <span data-id="element-228">Practice-led Program</span>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-229">
            <button onClick={onOpenOnboarding} className="group inline-flex items-center gap-2 px-8 py-4 text-lg font-semibold bg-brand-yellow text-dark-900 rounded-full hover:bg-brand-yellow-dark transition-all duration-300 hover:scale-105 glow-yellow" data-id="element-230">
              Start Learning Today
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-231" />
            </button>
            <a href="#curriculum" className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-white/80 hover:text-white border border-white/10 hover:border-white/30 rounded-full transition-all duration-200" data-id="element-232">
              <LayersIcon className="w-5 h-5" data-id="element-233" />
              View Curriculum
            </a>
          </motion.div>
        </div>

        {/* Interactive Dashboard Visual */}
        <motion.div initial={{
        opacity: 0,
        y: 60
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.5
      }} className="mt-20 relative" data-id="element-234">
          <div className="relative mx-auto max-w-4xl" data-id="element-235">
            {/* Glow behind dashboard */}
            <div className="absolute inset-0 bg-brand-yellow/5 rounded-3xl blur-2xl scale-95" data-id="element-236" />

            <HeroDashboard data-id="element-237" />

            {/* Floating stat cards */}
            <motion.div animate={{
            y: [0, -8, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -left-6 top-1/3 glass rounded-xl p-3 hidden lg:block" data-id="element-238">
              <div className="flex items-center gap-3" data-id="element-239">
                <div className="w-9 h-9 rounded-lg bg-brand-yellow/20 flex items-center justify-center" data-id="element-240">
                  <UsersIcon className="w-4 h-4 text-brand-yellow" data-id="element-241" />
                </div>
                <div data-id="element-242">
                  <p className="text-xs font-semibold text-white" data-id="element-243">
                    47 online now
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-244">in your cohort</p>
                </div>
              </div>
            </motion.div>

            <motion.div animate={{
            y: [0, 8, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -right-6 top-1/4 glass rounded-xl p-3 hidden lg:block" data-id="element-245">
              <div className="flex items-center gap-3" data-id="element-246">
                <div className="w-9 h-9 rounded-lg bg-green-500/20 flex items-center justify-center" data-id="element-247">
                  <ZapIcon className="w-4 h-4 text-green-400" data-id="element-248" />
                </div>
                <div data-id="element-249">
                  <p className="text-xs font-semibold text-white" data-id="element-250">
                    Mentor session
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-251">Today at 3:00 PM</p>
                </div>
              </div>
            </motion.div>

            <motion.div animate={{
            y: [0, -6, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute -right-6 bottom-1/4 glass rounded-xl p-3 hidden lg:block" data-id="element-252">
              <div className="flex items-center gap-3" data-id="element-253">
                <div className="w-9 h-9 rounded-full bg-brand-yellow flex items-center justify-center" data-id="element-254">
                  <span className="text-xs font-bold text-dark-900" data-id="element-255">SC</span>
                </div>
                <div data-id="element-256">
                  <p className="text-xs font-semibold text-white" data-id="element-257">Sarah Chen</p>
                  <p className="text-xs text-gray-500" data-id="element-258">Mentor · Google</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}